/**
 * @file App.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Main App component that serves as the root component for the Shay Suite Admin application.
 *              This file sets up the application routing, context providers, and defines all application routes.
 *              It wraps the entire application with necessary providers (Theme, Auth, Toast) and implements
 *              protected routing for authenticated sections of the application.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetails from './pages/CustomerDetails';
import SubscriptionPlans from './pages/SubscriptionPlans';
import ApiKeys from './pages/ApiKeys';
import ChatService from './pages/ChatService';

import Login from './pages/Login';
import Logout from './pages/Logout';



import AdminHub from './pages/AdminHub';
import Register from './pages/Register';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppTitle from './components/AppTitle';
import './App.css';

/**
 * Main App component that serves as the root of the application
 * 
 * This component:
 * - Sets up all necessary context providers (Theme, Auth, Toast)
 * - Configures Material-UI's CssBaseline for consistent styling
 * - Defines the application routing structure
 * - Implements protected routes for authenticated sections
 * - Handles both public and private routes
 * - Routes company admins to limited pages without sidebar
 * 
 * @returns {JSX.Element} The complete application structure with routing
 */
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <CssBaseline />
          <AppTitle />
          <Router>
            <Routes>
              {/* Public Routes - No authentication required */}
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              
              
              <Route path="/register" element={<Register />} />
              
              {/* Company Admin Routes - Limited access without sidebar */}
              
              
              {/* Root Admin Routes - Full access with sidebar */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/customers" element={
                <ProtectedRoute>
                  <Layout>
                    <Customers />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/customers/:customerId" element={
                <ProtectedRoute>
                  <Layout>
                    <CustomerDetails />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/subscription" element={
                <ProtectedRoute>
                  <Layout>
                    <SubscriptionPlans />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/apiKeys" element={
                <ProtectedRoute>
                  <Layout>
                    <ApiKeys />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/chatService" element={
                <ProtectedRoute>
                  <Layout>
                    <ChatService />
                  </Layout>
                </ProtectedRoute>
              } />
              <Route path="/adminHub" element={
                <ProtectedRoute>
                  <Layout>
                    <AdminHub />
                  </Layout>
                </ProtectedRoute>
              } />
            </Routes>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
